// Tiny IndexedDB wrapper for the player. We avoid pulling a full ORM and
// keep the interface narrow: read/write the active manifest, list/cache
// asset URL <-> asset key mappings, and report cache status.
//
// Schema (idb v2):
//  - meta:               key-value, holds { activeRevision, revisionMeta, status }
//  - assets:             keyPath = 'cacheKey'
//                        { cacheKey, assetId, revision, url, status, bytes, lastError }
//  - revisions:          keyPath = 'revisionId'
//                        { revisionId, screenId, revision, manifest, cachedAt, fullyCached }
//  - play_events_buffer: keyPath = 'clientEventId'
//                        Proof-of-Play events waiting to be flushed on the
//                        next heartbeat. FIFO eviction at PLAY_EVENT_BUFFER_CAP.

import { openDB, type IDBPDatabase } from 'idb';
import type { PlayerBundleV1 } from '@/types/schedule';

export interface CachedAssetRow {
  cacheKey: string;
  assetId: string;
  revisionId: string;
  url: string;
  status: 'pending' | 'cached' | 'failed';
  bytes: number;
  lastError?: string;
  cachedAt?: number;
}

export interface CachedRevisionRow {
  revisionId: string;
  screenId: string;
  revision: number;
  // Manifest is stored as the raw JSON to avoid type coupling.
  manifest: unknown;
  cachedAt: number;
  fullyCached: boolean;
}

export interface PlayerMeta {
  screenId: string;
  activeRevisionId: string | null;
  cacheStatus: 'idle' | 'syncing' | 'ready' | 'error';
  lastSyncAt?: number;
  lastError?: string;
}

const DB_NAME = 'signage-cache';
const DB_VERSION = 2;
const STORE_META = 'meta';
const STORE_ASSETS = 'assets';
const STORE_REVISIONS = 'revisions';
const STORE_PLAY_EVENTS = 'play_events_buffer';

let dbPromise: Promise<IDBPDatabase> | null = null;

export function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META);
        }
        if (!db.objectStoreNames.contains(STORE_ASSETS)) {
          const s = db.createObjectStore(STORE_ASSETS, { keyPath: 'cacheKey' });
          s.createIndex('byRevision', 'revisionId');
          s.createIndex('byStatus', 'status');
        }
        if (!db.objectStoreNames.contains(STORE_REVISIONS)) {
          db.createObjectStore(STORE_REVISIONS, { keyPath: 'revisionId' });
        }
        if (!db.objectStoreNames.contains(STORE_PLAY_EVENTS)) {
          const s = db.createObjectStore(STORE_PLAY_EVENTS, { keyPath: 'clientEventId' });
          s.createIndex('byCreatedAt', 'createdAt');
          s.createIndex('byScreenId', 'screenId');
        }
      },
    });
  }
  return dbPromise;
}

export { STORE_PLAY_EVENTS };

export async function getMeta(screenId: string): Promise<PlayerMeta | null> {
  const db = await getDb();
  return ((await db.get(STORE_META, `meta:${screenId}`)) as PlayerMeta | undefined) ?? null;
}

export async function setMeta(meta: PlayerMeta): Promise<void> {
  const db = await getDb();
  await db.put(STORE_META, meta, `meta:${meta.screenId}`);
}

export async function putRevision(row: CachedRevisionRow): Promise<void> {
  const db = await getDb();
  await db.put(STORE_REVISIONS, row);
}

export async function getRevision(revisionId: string): Promise<CachedRevisionRow | null> {
  const db = await getDb();
  return ((await db.get(STORE_REVISIONS, revisionId)) as CachedRevisionRow | undefined) ?? null;
}

export async function listRevisionsForScreen(screenId: string): Promise<CachedRevisionRow[]> {
  const db = await getDb();
  const all = (await db.getAll(STORE_REVISIONS)) as CachedRevisionRow[];
  return all.filter((r) => r.screenId === screenId);
}

export async function deleteRevision(revisionId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_REVISIONS, revisionId);
}

export async function listAssetsForRevision(revisionId: string): Promise<CachedAssetRow[]> {
  const db = await getDb();
  const tx = db.transaction(STORE_ASSETS, 'readonly');
  const idx = tx.store.index('byRevision');
  return (await idx.getAll(revisionId)) as CachedAssetRow[];
}

export async function deleteAssetsForRevision(revisionId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_ASSETS, 'readwrite');
  const idx = tx.store.index('byRevision');
  let cursor = await idx.openCursor(revisionId);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function putAsset(row: CachedAssetRow): Promise<void> {
  const db = await getDb();
  await db.put(STORE_ASSETS, row);
}

export async function getAsset(cacheKey: string): Promise<CachedAssetRow | null> {
  const db = await getDb();
  return ((await db.get(STORE_ASSETS, cacheKey)) as CachedAssetRow | undefined) ?? null;
}

/**
 * Persist the most recent player bundle (default revision + schedule windows
 * for all enabled scheduled revisions). Stored in `meta` so the player can
 * boot offline and immediately evaluate which revision to play.
 */
export async function setBundle(screenId: string, bundle: PlayerBundleV1): Promise<void> {
  const db = await getDb();
  await db.put(STORE_META, bundle, `bundle:${screenId}`);
}

export async function getBundle(screenId: string): Promise<PlayerBundleV1 | null> {
  const db = await getDb();
  return ((await db.get(STORE_META, `bundle:${screenId}`)) as PlayerBundleV1 | undefined) ?? null;
}
